import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { EVENT_QUERY_KEYS } from './keys';

import * as eventService from 'services/events';

import { handleError } from 'utils/errorHandler';

import type { Events } from 'interface/events';
import type { ServiceId, ServiceParams } from 'interface/service';

type EventListResponse = { data?: Events[]; meta?: { page?: number; total?: number } };

export const getEventsQueryKey = (params: ServiceParams = {}) => [EVENT_QUERY_KEYS.LIST, params] as const;

export const eventsQueryOptions = (params: ServiceParams = {}) => ({
  queryKey: getEventsQueryKey(params),
  queryFn: () => eventService.fetchEvents(params) as Promise<EventListResponse>,
});

export const useFetchEventsQuery = (
  params: ServiceParams = {},
  enabled = true
): UseQueryResult<EventListResponse, Error> => {
  return useQuery({
    ...eventsQueryOptions(params),
    enabled,
  });
};

export const getEventByIdQueryKey = (id?: ServiceId) => [EVENT_QUERY_KEYS.DETAIL, id] as const;

export const useFetchEventByIdQuery = <TData = Events>(
  id?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getEventByIdQueryKey(id),
    queryFn: () => eventService.fetchEventById(id!) as Promise<TData>,
    enabled: Boolean(id) && enabled,
  });
};

const useInvalidateEvents = () => {
  const queryClient = useQueryClient();

  return {
    invalidateList: () => queryClient.invalidateQueries({ queryKey: [EVENT_QUERY_KEYS.LIST] }),
    invalidateDetail: (id: ServiceId) => queryClient.invalidateQueries({ queryKey: getEventByIdQueryKey(id) }),
  };
};

const handleMutationError = (error: object) => handleError(error);

export const useCreateEventMutation = () => {
  const { invalidateList } = useInvalidateEvents();

  return useMutation({
    mutationFn: (event: Events) => eventService.createEvent(event),
    onSuccess: invalidateList,
    onError: handleMutationError,
  });
};

export const useUpdateEventMutation = () => {
  const { invalidateList, invalidateDetail } = useInvalidateEvents();

  return useMutation({
    mutationFn: ({ id, event }: { id: ServiceId; event: Events }) => eventService.updateEvent(id, event),
    onSuccess: (_data, { id }) => {
      invalidateList();
      invalidateDetail(id);
    },
    onError: handleMutationError,
  });
};

export const useDeleteEventMutation = () => {
  const { invalidateList } = useInvalidateEvents();

  return useMutation({
    mutationFn: (id: ServiceId) => eventService.removeEvent(id),
    onSuccess: invalidateList,
    onError: handleMutationError,
  });
};
